import { useCallback, useContext, useDebugValue } from 'react';
import * as StorefrontApiService from '@root/lib/services/StorefrontApiService';
import context from '.';

const toMoney = StorefrontApiService.decimalToCents

const FRAGMENTS = {
  money: {query: [''], transformer: () => {}},
  product: {query: [''], transformer: () => {}},
  variant: {query: [''], transformer: () => {}},
  discountApplication: {query: [''], transformer: () => {}},
  discountAllocation: {query: [''], transformer: () => {}},
  lineItem: {query: [''], transformer: () => {}},
  shippingAddress: {query: [''], transformer: () => {}},
  checkout: {query: [''], transformer: () => {}},
};

FRAGMENTS.money = {
  query: [
    'amount',
  ],
  transformer: (node) => toMoney(node.amount),
};

FRAGMENTS.product = {
  query: [
    'id',
  ],
  transformer: (node) => ({
    ...node,
    id: toNumericId(node.id),
  }),
};

FRAGMENTS.variant = {
  query: [
    'id',
    'price: priceV2', FRAGMENTS.money.query,
    'sku',
    'title',
    'product', FRAGMENTS.product.query,
    'quantityAvailable',
    'images: image', [
      'alt: altText',
      'thumbnail: transformedSrc(maxWidth: 100)',
    ]
  ],
  transformer: (node) => ({
    ...node,
    id: toNumericId(node.id),
    price: FRAGMENTS.money.transformer(node.price),
    product: FRAGMENTS.product.transformer(node.product),
  }),
};

FRAGMENTS.discountApplication = {
  query: [
    'allocationMethod',
    'targetSelection',
    'targetType',
    'type: __typename',
    'value', [
      'type: __typename',
      '... on MoneyV2', FRAGMENTS.money.query,
      '... on PricingPercentageValue', [
        'percentage',
      ],
    ],
    '... on DiscountCodeApplication', [
      'applicable',
      'code',
    ],
    '... on AutomaticDiscountApplication', [
      'title',
    ],
  ],
  transformer: (node) => {
    const isPercent = node.value.type !== 'MoneyV2';
    const valueField = !isPercent ? 'amount' : 'percentage';
    return {
      ...node,
      value: {
        ...node.value,
        [valueField]: !isPercent
          ? FRAGMENTS.money.transformer(node.value)
          : parseFloat(node.value.percentage),
      }
    };
  }
};

FRAGMENTS.discountAllocation = {
  query: [
    'allocatedAmount', FRAGMENTS.money.query,
    'discountApplication', FRAGMENTS.discountApplication.query,
  ],
  transformer: (node) => ({
    ...node,
    allocatedAmount: FRAGMENTS.money.transformer(node.allocatedAmount),
    discountApplication: FRAGMENTS.discountApplication.transformer(node.discountApplication),
  }),
}

FRAGMENTS.lineItem = {
  query: [
    'id',
    'quantity',
    'title',
    'unitPrice', FRAGMENTS.money.query,
    'variant', FRAGMENTS.variant.query,
    'discounts: discountAllocations', FRAGMENTS.discountAllocation.query,
    'properties: customAttributes', [
      'name: key',
      'value',
    ],
  ],
  transformer: (node) => ({
      ...node,
      unitPrice: FRAGMENTS.money.transformer(node.unitPrice ?? node.variant.price),
      variant: FRAGMENTS.variant.transformer(node.variant),
      discounts: node.discounts.map(FRAGMENTS.discountAllocation.transformer),
      properties: node.properties.reduce((props, prop) => ({
        ...props,
        [prop.name]: prop.value,
      }), {}),
  }),
};

FRAGMENTS.shippingAddress = {
  query: [
    'address1',
    'address2',
    'city',
    'company',
    'country',
    'countryCode: countryCodeV2',
    'firstName',
    'lastName',
    'zip',
    'province',
    'provinceCode',
    'phone',    
  ],
  transformer: (node) => node,
};

FRAGMENTS.checkout = {
  query: [
    'id',
    'total: totalPriceV2', FRAGMENTS.money.query,
    'totalTax: totalTaxV2', FRAGMENTS.money.query,
    'subtotal: lineItemsSubtotalPrice', FRAGMENTS.money.query,
    'shippingDiscounts: shippingDiscountAllocations', FRAGMENTS.discountAllocation.query,
    'shippingAddress', FRAGMENTS.shippingAddress.query,
    'discounts: discountApplications (first: 20)', [
      'edges', [
        'discount: node', FRAGMENTS.discountApplication.query,
      ]
    ],
    'lineItems (first: 100)', [
      'edges', [
        'lineItem: node', FRAGMENTS.lineItem.query,
      ],
    ],
  ],
  transformer: (node) => ({
    ...node,
    total: FRAGMENTS.money.transformer(node.total),
    totalTax: FRAGMENTS.money.transformer(node.totalTax),
    subtotal: FRAGMENTS.money.transformer(node.subtotal),
    shippingDiscounts: node.shippingDiscounts.map(FRAGMENTS.discountAllocation.transformer),
    lineItems: node.lineItems.edges.map(n => FRAGMENTS.lineItem.transformer(n.lineItem)),
    discounts: node.discounts.edges.map(n => FRAGMENTS.discountApplication.transformer(n.discount)),
    shippingAddress: FRAGMENTS.shippingAddress.transformer(node.shippingAddress),
  }),
};

/**
 * Converts a GraphQL id into a numeric id
 *
 * @param {string} str
 */
const toNumericId = (str) => {
  const [ ,id ] = str.match(/^gid:\/\/shopify\/.+\/(\d+)$/);
  return parseInt(id);
}

/**
 * Provides access to a local cart and APIs to interact with the cart
 */
const useCart = () => {
  const { state, stateRef, setState, functions } = useContext(context);
  const { cart } = state;

  /**
   * Adds pending processes to the pending queue and removes them once the process
   * is done to show what is being modified
   */
  const withPending = useCallback(async (type, data, fn) => {
    const id = Symbol();
    setState(ps => ({
      ...ps,
      cart: {
        ...ps.cart,
        pending: [
          ...ps.cart.pending,
          {id, type, data},
        ],
      },
    }));

    try {
      return await fn();
    } finally {
      setState(ps => ({
        ...ps,
        cart: {
          ...ps.cart,
          pending: ps.cart.pending.filter(p => p.id !== id),
        },
      }));
    }
  }, []);

  /**
   * Sets the cart state
   */
  const set = useCallback(async (cart) => {
    const newCart = await functions.processCart(cart);
    setState(ps => ({
      ...ps,
      cart: {
        ...ps.cart,
        originalState: cart,
        state: newCart,
      }
    }));
  }, []);

  /**
   * Gets the discount code on the cart if there is one
   *
   * @returns {undefined|{
   *   code: string;
   *   applicable: boolean;
   * }}
   */
  const getDiscountCode = useCallback(() => {
    const { cart } = stateRef.current;
    return cart.state?.discounts?.find?.(d => 'code' in d);
  }, []);

  /**
   * Determines if the cart has pending actions on it of the type provided.
   * If no type provided then function will check for any pending operations
   */
  const isLoading = useCallback((...types) => {
    const { cart } = stateRef.current;

    return types.length === 0
      ? cart.pending.length > 0
      : cart.pending.some(p => types.some(type => p.type === type));
  }, []);

  /**
   * Sets the discount code on the cart
   */
  const setDiscountCode = useCallback(async (code) => {
    const { cart } = stateRef.current;
    if (!cart.state?.id) {
      console.warn('setDiscountCode attempted on uninitialized cart');
      return;
    }

    await withPending('discountCode', {code}, async () => {
      const checkout = await StorefrontApiService
        .setCheckoutDiscountCode(cart.state.id, code, FRAGMENTS.checkout.query)
        .then(FRAGMENTS.checkout.transformer);
      await set(checkout);
    });
  }, []);

  /**
   * Removes the set discount code on the order
   */
  const removeDiscountCode = useCallback(async () => {
    const { cart } = stateRef.current;

    if (!cart.state?.id) {
      console.warn('removeDiscountCode attempted on uninitialized cart');
      return;
    }
    await withPending('discountCode', undefined, async () => {
      const checkout = await StorefrontApiService
        .removeCheckoutDiscountCode(cart.state.id, FRAGMENTS.checkout.query)
        .then(FRAGMENTS.checkout.transformer);
      await set(checkout);
    });
  }, []);

  /**
   * Adds an item to the cart
   */
  const addItems = useCallback(async (...items) => {
    const { cart } = stateRef.current;

    if (!cart.state?.id) {
      console.warn('AddItems attempted on uninitialized cart');
      return;
    }

    await withPending('addItems', {items}, async () => {
      const checkout = await StorefrontApiService
        .addItemsToCheckout(cart.state.id, items, FRAGMENTS.checkout.query)
        .then(FRAGMENTS.checkout.transformer);
      await set(checkout);
    });
  }, []);

  /**
   * Updates the shipping address on the cart
   */
  const updateShippingAddress = useCallback(async (address) => {
    const { cart } = stateRef.current;

    if (!cart.state?.id) {
      console.warn('AddItems attempted on uninitialized cart');
      return;
    }

    await withPending('shippingAddress', {address}, async () => {
      const checkout = await StorefrontApiService
        .updateShippingAddressOnCheckout(cart.state.id, address, FRAGMENTS.checkout.query)
        .then(FRAGMENTS.checkout.transformer);
      await set(checkout);
    });
  }, []);

  /**
   * Updates existing line items in the cart
   */
  const updateItems = useCallback(async (...items) => {
    const { cart } = stateRef.current;

    if (!cart.state?.id) {
      console.warn('updateItems attempted on uninitialized cart');
      return;
    }

    await withPending('updateItems', {items}, async () => {
      const checkout = await StorefrontApiService
        .updateItemsInCheckout(cart.state.id, items, FRAGMENTS.checkout.query)
        .then(FRAGMENTS.checkout.transformer);
      await set(checkout);
    });
  }, []);

    /**
     * Removes all the line items from the cart
     */
  const clear = useCallback(async () => {
    const { cart } = stateRef.current;

    if (!cart.state?.id) {
      console.warn('Clear attempted on uninitialized cart');
      return
    }

    await withPending('clear', null, async () => {
      const checkout = await StorefrontApiService
        .replaceCheckoutLineItems(cart.state.id, [], FRAGMENTS.checkout.query)
        .then(FRAGMENTS.checkout.transformer);
      await set(checkout);
    });
  }, []);

    /**
     * Hydrates the cart with data from Shopify. If the cart is not set or
     * the cart.id is not set, function will no-op
     */
  const hydrate = useCallback(async () => {
    const { cart } = stateRef.current;

    if (!cart.state?.id) {
      console.warn('Hydrate attempted on undefined cart');
      return;
    }

    await withPending('init', null, async () => {
      const checkout = await StorefrontApiService
        .getNodeById(cart.state.id, 'Checkout', FRAGMENTS.checkout.query)
        .then(FRAGMENTS.checkout.transformer);
      await set(checkout);
    });
  }, []);

  /**
   * Initializes a new cart. Existing cart will be overriden if one exists
   */
  const init = useCallback(async () => {
    await withPending('init', null, async () => {
      const checkout = await StorefrontApiService
        .initializeCheckout(FRAGMENTS.checkout.query)
        .then(FRAGMENTS.checkout.transformer);
      await set(checkout);
    });
  }, []);

  /**
   * Runs the cart through PRE again and sets the then
   */
  const reprocess = useCallback(async () => {
    const { cart } = stateRef.current;
    await set(cart.originalState);
  }, []);

  useDebugValue(cart);
  return [
    cart.state,
    {
      getDiscountCode,
      isLoading,
      setDiscountCode,
      removeDiscountCode,
      addItems,
      updateItems,
      clear,
      set,
      hydrate,
      init,
      reprocess,
      updateShippingAddress,
    }
  ];
}

export default useCart;
